import * as React from 'react';
import { useSelector } from 'react-redux';
import { useTranslations } from '../../../../store/translations/hooks';
import { TK } from '../../../../store/translations/translationKeys';
import { AnalysisResult } from '../../../Clients/ImportClients/useAnalyseCSV';
import { ColumnMapping, normalizeHeader } from '../../../Clients/ImportClients/autoMap';
import { getEntitiesDataItems } from '../../../../store/entities/selectors';
import { ProfessionalEntity } from '../../../../store/entities/state';
import { ImportAppointmentOptions } from './ImportAppointmentOptions';

const SKIP = 'skip' as const;

// Re-use normalizeHeader as normalizeLookup — same operation (lowercase, strip diacritics, collapse spaces)
const normalizeLookup = normalizeHeader;

interface Props {
    result: AnalysisResult;
    mapping: ColumnMapping;
    options: ImportAppointmentOptions;
    onOptionsChange: (o: ImportAppointmentOptions) => void;
}

const ProfessionalMappingStep: React.FC<Props> = ({ result, mapping, options, onOptionsChange }) => {
    const t = useTranslations();
    const professionals = useSelector(getEntitiesDataItems<ProfessionalEntity>('professionals'));

    const profColIndex = mapping.fieldMap['professional']?.[0] ?? -1;

    // All unique raw names that appear in the professional column
    const uniqueNames = React.useMemo(() => {
        if (profColIndex === -1) return [];
        const seen = new Set<string>();
        const names: string[] = [];
        for (const row of result.allRows) {
            const v = row[profColIndex]?.trim();
            if (v && !seen.has(v)) { seen.add(v); names.push(v); }
        }
        return names;
    }, [result.allRows, profColIndex]);

    // Normalized-name → professional lookup
    const profByNormName = React.useMemo(() => {
        const map = new Map<string, ProfessionalEntity>();
        for (const p of professionals) map.set(normalizeLookup(p.name), p);
        return map;
    }, [professionals]);

    // On first render for a given set of names, auto-suggest matches not yet in options
    const initializedRef = React.useRef(false);
    React.useEffect(() => {
        if (initializedRef.current) return;
        initializedRef.current = true;

        const updated: Record<string, string | 'skip'> = { ...options.professionalMappings };
        let changed = false;
        for (const name of uniqueNames) {
            const norm = normalizeLookup(name);
            if (norm in updated) continue;
            const match = profByNormName.get(norm);
            if (match) { updated[norm] = match.id; changed = true; }
        }
        if (changed) onOptionsChange({ ...options, professionalMappings: updated });
    }, [uniqueNames, profByNormName]);

    const handleSelect = (name: string, value: string) => {
        const norm = normalizeLookup(name);
        onOptionsChange({
            ...options,
            professionalMappings: { ...options.professionalMappings, [norm]: value },
        });
    };

    if (uniqueNames.length === 0) {
        return (
            <div style={{ color: 'var(--text-muted)', padding: 'var(--spacer-M)' }}>
                —
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
            <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', margin: 0 }}>
                {t(TK.importAppointmentsMapProfessionals)}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', overflowY: 'auto', maxHeight: '60vh' }}>
                {uniqueNames.map(name => {
                    const norm = normalizeLookup(name);
                    const currentValue = options.professionalMappings[norm] ?? '';
                    const isUnresolved = currentValue === '';
                    return (
                        <div
                            key={name}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacer-S)',
                                padding: 'var(--spacer-XS) var(--spacer-S)',
                                borderRadius: '6px',
                                border: `var(--border-thin) solid ${isUnresolved ? 'var(--warning-color)' : 'var(--appLigthGrey)'}`,
                                background: isUnresolved ? 'var(--warning-bg)' : 'white',
                            }}
                        >
                            <span style={{
                                flex: 1,
                                fontSize: '0.875rem',
                                color: 'var(--text-body)',
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}>
                                {name}
                            </span>
                            <select
                                value={currentValue}
                                onChange={e => handleSelect(name, e.target.value)}
                                style={{
                                    fontSize: '0.85rem',
                                    padding: '4px var(--spacer-XS)',
                                    border: `var(--border-thin) solid ${isUnresolved ? 'var(--warning-color)' : 'var(--appLigthGrey)'}`,
                                    borderRadius: '4px',
                                    color: isUnresolved ? 'var(--text-placeholder)' : 'var(--text-body)',
                                    background: 'white',
                                    minWidth: '220px',
                                    maxWidth: '320px',
                                }}
                            >
                                <option value="" disabled>{t(TK.select)}…</option>
                                <option value={SKIP}>{t(TK.importAppointmentsSkipAppointments)}</option>
                                <option disabled>──────────</option>
                                {professionals.map(p => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                            </select>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ProfessionalMappingStep;

// Pure helper exported so the modal can gate the Next button without re-implementing logic
export function areProfessionalsMapped(
    uniqueNames: string[],
    professionalMappings: Record<string, string | 'skip'>,
): boolean {
    return uniqueNames.every(name => normalizeLookup(name) in professionalMappings);
}

export function getUniqueProfessionalNames(
    allRows: string[][],
    profColIndex: number,
): string[] {
    if (profColIndex === -1) return [];
    const seen = new Set<string>();
    const names: string[] = [];
    for (const row of allRows) {
        const v = row[profColIndex]?.trim();
        if (v && !seen.has(v)) { seen.add(v); names.push(v); }
    }
    return names;
}
